/**
 * @fileoverview Organizer Profile Form component.
 * @summary Form for editing organizer profile (firstName, lastName only).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/ProfileFormOrganizer
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import FloatingLabelInput from '../../../components/FloatingLabelInput'
import Spinner from '../../../components/Spinner'

interface ProfileFormOrganizerProps {
  firstName: string
  lastName: string
  organization: string
  email: string
  pendingEmail?: string
  isSaving: boolean
  onFirstNameChange: (value: string) => void
  onLastNameChange: (value: string) => void
  onOrganizationChange: (value: string) => void
  onEmailChange: (value: string) => void
  onSave: () => void
}

/**
 * @summary Organizer profile form component.
 * @param {ProfileFormOrganizerProps} props - Component props.
 * @returns {React.ReactElement} Organizer profile form.
 */
export function ProfileFormOrganizer({
  firstName,
  lastName,
  organization,
  email,
  pendingEmail,
  isSaving,
  onFirstNameChange,
  onLastNameChange,
  onOrganizationChange,
  onEmailChange,
  onSave,
}: ProfileFormOrganizerProps) {
  const { t } = useTranslation()

  return (
    <div className="card bg-base-100 dark:bg-base-800 shadow-lg">
      <div className="card-body">
        <h2 className="card-title text-xl mb-4">{t('organizer.editProfile')}</h2>

        <div className="space-y-4">
          <FloatingLabelInput
            id="firstName"
            name="firstName"
            label={t('auth.firstName')}
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
          />

          <FloatingLabelInput
            id="lastName"
            name="lastName"
            label={t('auth.lastName')}
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
          />

          <FloatingLabelInput
            id="organization"
            name="organization"
            label={t('auth.organization')}
            value={organization}
            onChange={(e) => onOrganizationChange(e.target.value)}
          />

          <FloatingLabelInput
            id="email"
            name="email"
            label={t('auth.email')}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
          />

          {pendingEmail && (
            <div className="p-3 bg-warning/10 border border-warning rounded-lg">
              <p className="text-sm text-warning font-semibold">⏳ {t('email.validationPending')}</p>
              <p className="text-xs text-warning/80 mt-1">{t('email.pendingEmailAddress')}: {pendingEmail}</p>
            </div>
          )}

          <button
            className="btn btn-primary w-full"
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? <Spinner size="sm" className="text-white!" /> : t('singer.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  )
}

